import { Avatar, Stack, Typography } from "@mui/material";
import type { GridColDef } from "@mui/x-data-grid-premium";
import GridTextHoverLink from "@entrilia/shared/components/grid/GridTextHoverLink";
import EntriliaLogo from "@entrilia/shared/images/greylogo.png";
import { convertISODateTime } from "@entrilia/shared/utilities/dateUtils";
import { nameof } from "@entrilia/shared/utilities/typeHelper";
import type { Field, ObjectFieldValues } from "../../../api/types/objectTypes";
import { objectTimestampsColumnIds, type TableColumnDefinition } from "../../common/columns/columnDefinitions";
import { getGridColumnForField, hasInteractiveGridCell } from "../fieldGridDefinitions";

interface Options {
  getDetailsPath: (id: string) => string;
  detailsNavigationState?: unknown;
  showLogos?: boolean;
  showDuplicateCaptions?: boolean;
}

const getStaticColumns = ({ showLogos }: Options): GridColDef<ObjectFieldValues>[] => {
  const staticColumns: GridColDef<ObjectFieldValues>[] = [];

  if (showLogos) {
    staticColumns.push({
      field: nameof<ObjectFieldValues>("logoUrl"),
      headerName: "",
      sortable: false,
      resizable: false,
      width: 72,
      renderCell: ({ row }) => (
        <Avatar
          src={row.logoUrl || EntriliaLogo}
          variant="rounded"
          alt="logo"
          slotProps={{ img: { sx: { objectFit: "contain" } } }}
          sx={{ width: 60, height: 24 }}
        />
      ),
    });
  }

  return staticColumns;
};

const getCreatedAtColumn = (): GridColDef<ObjectFieldValues> => ({
  field: nameof<ObjectFieldValues>("createdAt"),
  headerName: "Created at",
  sortable: false,
  flex: 1,
  minWidth: 160,
  valueFormatter: (value: string | undefined) => convertISODateTime(value),
});

const getModifiedAtColumn = (): GridColDef<ObjectFieldValues> => ({
  field: nameof<ObjectFieldValues>("updateAt"),
  headerName: "Modified at",
  sortable: false,
  flex: 1,
  minWidth: 160,
  valueFormatter: (value: string | undefined) => convertISODateTime(value),
});

const withDetailsLink = (
  colDef: GridColDef<ObjectFieldValues>,
  { getDetailsPath, detailsNavigationState }: Options
): GridColDef<ObjectFieldValues> => {
  const renderValue = colDef.renderCell;

  return {
    ...colDef,
    renderCell: (params) => (
      <GridTextHoverLink to={getDetailsPath(params.row.id)} state={detailsNavigationState}>
        {renderValue ? renderValue(params) : params.formattedValue}
      </GridTextHoverLink>
    ),
  };
};

interface ColumnCandidate {
  colDef: GridColDef<ObjectFieldValues>;
  canHostDetailsLink: boolean;
}

const withDuplicateCaption = (colDef: GridColDef<ObjectFieldValues>): GridColDef<ObjectFieldValues> => {
  const renderValue = colDef.renderCell;

  return {
    ...colDef,
    renderCell: (params) => {
      const value = renderValue ? renderValue(params) : params.formattedValue;

      return params.row.hasPossibleDuplicates ? (
        <Stack>
          {value}
          <Typography variant="caption" color="warning">
            Possible duplicate
          </Typography>
        </Stack>
      ) : (
        value
      );
    },
  };
};

const getColumnCandidates = (fields: Field[], visibleColumns: TableColumnDefinition[]): ColumnCandidate[] => {
  return visibleColumns.reduce<ColumnCandidate[]>((result, column) => {
    if (column.id === objectTimestampsColumnIds.createdAtColumnId) {
      result.push({ colDef: getCreatedAtColumn(), canHostDetailsLink: true });
      return result;
    }

    if (column.id === objectTimestampsColumnIds.modifiedAtColumnId) {
      result.push({ colDef: getModifiedAtColumn(), canHostDetailsLink: true });
      return result;
    }

    const field = fields.find((field) => field.id === column.id);
    if (field) {
      const colDef: GridColDef<ObjectFieldValues> = getGridColumnForField(field);
      result.push({ colDef, canHostDetailsLink: !hasInteractiveGridCell(field.type) });
    }
    return result;
  }, []);
};

const getColumnsForAllEntityFields = (
  fields: Field[],
  visibleColumns: TableColumnDefinition[],
  options: Options
): GridColDef<ObjectFieldValues>[] => {
  const candidates = getColumnCandidates(fields, visibleColumns);
  const linkColumnIndex = candidates.findIndex((candidate) => candidate.canHostDetailsLink);

  return candidates.map(({ colDef }, i) => {
    if (i !== linkColumnIndex) {
      return colDef;
    }

    return withDetailsLink(options.showDuplicateCaptions ? withDuplicateCaption(colDef) : colDef, options);
  });
};

export const getEntitiesGridColumns = (
  entityFields: Field[],
  visibleColumns: TableColumnDefinition[],
  options: Options
): GridColDef<ObjectFieldValues>[] => [
  ...getStaticColumns(options),
  ...getColumnsForAllEntityFields(entityFields, visibleColumns, options),
];
